/**
 * Barra superior Crearte (sections/crearte-topbar.liquid).
 * - Menú móvil desplegable bajo la barra, con acordeones (<details>).
 * - Buscador móvil desplegable.
 * - Contador del carrito sincronizado.
 * - Notificación al agregar un producto al carrito (reemplaza al carrito lateral).
 */
(() => {
  if (window.CrearteTopbar) return;
  window.CrearteTopbar = true;

  const NOTIFICATION_DURATION = 6000;

  /** @param {HTMLElement} root */
  function initTopbar(root) {
    const menuToggle = root.querySelector('[data-crearte-menu-toggle]');
    const menuPanel = root.querySelector('[data-crearte-menu]');
    const searchToggle = root.querySelector('[data-crearte-search-toggle]');
    const searchPanel = root.querySelector('[data-crearte-search]');

    const setOpen = (toggle, panel, open) => {
      if (!toggle || !panel) return;
      toggle.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      panel.classList.toggle('is-open', open);
    };

    const closeAll = () => {
      setOpen(menuToggle, menuPanel, false);
      setOpen(searchToggle, searchPanel, false);
      document.body.classList.remove('crearte-menu-open');
    };

    menuToggle?.addEventListener('click', () => {
      const open = menuToggle.getAttribute('aria-expanded') !== 'true';
      closeAll();
      setOpen(menuToggle, menuPanel, open);
      document.body.classList.toggle('crearte-menu-open', open);
      hideNotification();
    });

    searchToggle?.addEventListener('click', () => {
      const open = searchToggle.getAttribute('aria-expanded') !== 'true';
      closeAll();
      setOpen(searchToggle, searchPanel, open);
      if (open) searchPanel?.querySelector('input[type="search"]')?.focus();
      hideNotification();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAll();
    });

    // Al pasar a escritorio se cierran los paneles móviles.
    window.matchMedia('(min-width: 750px)').addEventListener('change', (event) => {
      if (event.matches) closeAll();
    });
  }

  /* ---------- Contador del carrito ---------- */

  /** @param {number} count */
  function renderCartCount(count) {
    document.querySelectorAll('[data-crearte-cart-count]').forEach((el) => {
      el.textContent = String(count);
    });
  }

  /* ---------- Notificación de producto agregado ---------- */

  let hideTimer;

  function getNotification() {
    return /** @type {HTMLElement | null} */ (document.querySelector('[data-crearte-cart-notification]'));
  }

  function hideNotification() {
    const el = getNotification();
    if (!el) return;
    clearTimeout(hideTimer);
    el.classList.remove('is-visible');
    el.setAttribute('aria-hidden', 'true');
  }

  /** @param {number} cents */
  function formatMoney(cents) {
    const el = getNotification();
    const currency = el?.dataset.currency || 'CLP';
    const locale = el?.dataset.locale || 'es-CL';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(0);
    }
  }

  /** @param {string} value */
  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }

  /**
   * @param {any} item - Línea del carrito (formato /cart.js).
   * @param {number} quantityAdded
   */
  function showNotification(item, quantityAdded) {
    const el = getNotification();
    if (!el || !item) return;

    const image = item.featured_image?.url || item.image;
    const imageUrl = image ? `${image}${image.includes('?') ? '&' : '?'}width=160` : '';
    const variant = item.variant_title && !item.product_has_only_default_variant ? item.variant_title : '';
    const quantity = quantityAdded || item.quantity;

    const body = el.querySelector('[data-crearte-cart-notification-body]');
    if (body) {
      body.innerHTML = `
        ${imageUrl ? `<img class="crearte-cart-notification__image" src="${imageUrl}" alt="" width="80" height="80" loading="lazy">` : ''}
        <div class="crearte-cart-notification__info">
          <a class="crearte-cart-notification__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${variant ? `<p class="crearte-cart-notification__variant">${escapeHtml(variant)}</p>` : ''}
          <p class="crearte-cart-notification__price">${quantity} × ${formatMoney(item.final_price)}</p>
        </div>
      `;
    }

    el.classList.add('is-visible');
    el.setAttribute('aria-hidden', 'false');
    clearTimeout(hideTimer);
    hideTimer = setTimeout(hideNotification, NOTIFICATION_DURATION);
  }

  /** @param {unknown} merchandiseId */
  function toVariantId(merchandiseId) {
    if (merchandiseId == null) return null;
    const match = String(merchandiseId).match(/(\d+)$/);
    return match ? Number(match[1]) : null;
  }

  document.addEventListener(
    'shopify:cart:lines-update',
    (event) => {
      const cartEvent = /** @type {any} */ (event);
      const promise = cartEvent.promise;
      if (!promise || typeof promise.then !== 'function') return;

      promise
        .then((/** @type {any} */ result) => {
          const count = result?.cart?.totalQuantity;
          if (typeof count === 'number') renderCartCount(count);

          if (cartEvent.action !== 'add') return;
          const detail = result?.detail;
          if (!detail || detail.didError) return;

          const items = Array.isArray(detail.items) ? detail.items : [];
          const line = cartEvent.lines?.[0];
          const variantId = toVariantId(line?.merchandiseId);
          const item = items.find((i) => i.variant_id === variantId || i.id === variantId) || items[0];
          showNotification(item, line?.quantity);
        })
        .catch(() => {});
    },
    true
  );

  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const el = getNotification();
    if (!el || !el.classList.contains('is-visible')) return;
    if (target.closest('[data-crearte-cart-notification-close]')) {
      hideNotification();
      return;
    }
    // Clic fuera de la notificación la cierra (salvo que sea el botón que agregó el producto).
    if (!el.contains(target) && !target.closest('add-to-cart-component, product-form-component, [name="add"]')) {
      hideNotification();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hideNotification();
  });

  /* ---------- Inicio ---------- */

  const init = () => {
    document.querySelectorAll('[data-crearte-topbar]').forEach((root) => initTopbar(/** @type {HTMLElement} */ (root)));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Editor de temas: reinicia al recargar la sección.
  document.addEventListener('shopify:section:load', (event) => {
    const root = /** @type {HTMLElement} */ (event.target).querySelector?.('[data-crearte-topbar]');
    if (root) initTopbar(/** @type {HTMLElement} */ (root));
  });
})();
